import * as TabsPrimitive from "@radix-ui/react-tabs"
import type { ComponentProps } from "react"

import { cn } from "~/lib/utils"

function Tabs({
	className,
	...props
}: ComponentProps<typeof TabsPrimitive.Root>) {
	return (
		<TabsPrimitive.Root
			data-slot="tabs"
			className={cn("flex flex-col gap-2", className)}
			{...props}
		/>
	)
}

function TabsList({
	className,
	variant = "default",
	stretch = false,
	wrap = false,
	...props
}: ComponentProps<typeof TabsPrimitive.List> & {
	variant?: "default" | "navigation"
	stretch?: boolean
	wrap?: boolean
}) {
	return (
		<TabsPrimitive.List
			data-slot="tabs-list"
			data-variant={variant}
			data-stretch={stretch || undefined}
			className={cn(
				"group/tabs-list text-text-main inline-flex gap-1 rounded-lg",
				variant === "default" &&
					"border border-button-edge shadow-button-pressed bg-background-tertiary h-9 w-fit items-center justify-center p-1",
				variant === "navigation" &&
					"h-auto w-max justify-start p-0 md:w-full md:flex-col",
				stretch && "w-full *:data-[slot=tabs-trigger]:flex-1",
				wrap && "h-auto flex-wrap",
				className
			)}
			{...props}
		/>
	)
}

function TabsTrigger({
	className,
	...props
}: ComponentProps<typeof TabsPrimitive.Trigger>) {
	return (
		<TabsPrimitive.Trigger
			data-slot="tabs-trigger"
			className={cn(
				"border border-transparent data-[state=active]:control-raised control-pressable hover:border-button-edge hover:bg-active hover:shadow-button data-[state=active]:bg-active ring-ring/10 dark:ring-ring/20 dark:outline-ring/40 outline-ring/50 inline-flex items-center justify-center gap-2 rounded-md px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-4 focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 aria-invalid:focus-visible:ring-0 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 cursor-pointer",
				"group-data-[variant=navigation]/tabs-list:shrink-0 group-data-[variant=navigation]/tabs-list:justify-start group-data-[variant=navigation]/tabs-list:gap-3 group-data-[variant=navigation]/tabs-list:rounded-lg group-data-[variant=navigation]/tabs-list:px-3 group-data-[variant=navigation]/tabs-list:py-3 group-data-[variant=navigation]/tabs-list:text-text-sub group-data-[variant=navigation]/tabs-list:data-[state=active]:bg-background-elevated group-data-[variant=navigation]/tabs-list:data-[state=active]:text-text-main md:group-data-[variant=navigation]/tabs-list:w-full",
				className
			)}
			{...props}
		/>
	)
}

function TabsContent({
	className,
	...props
}: ComponentProps<typeof TabsPrimitive.Content>) {
	return (
		<TabsPrimitive.Content
			data-slot="tabs-content"
			className={cn(
				"ring-ring/10 dark:ring-ring/20 dark:outline-ring/40 outline-ring/50 flex-1 transition-[color,box-shadow] focus-visible:ring-4 focus-visible:outline-1 aria-invalid:focus-visible:ring-0",
				className
			)}
			{...props}
		/>
	)
}

export { Tabs, TabsContent, TabsList, TabsTrigger }
